/**
 * Nmbrs Sync Status Hooks
 *
 * React Query hooks for the Nmbrs sync status dashboard and manual sync triggers.
 * Story Nmbrs-2.2: Sync Status Dashboard
 * Story Nmbrs-2.4: Handmatige Sync Trigger
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../../../lib/api';

// ============================================
// Types
// ============================================

export type HealthStatus = 'healthy' | 'warning' | 'error';

export interface SyncStatusResponse {
  data: {
    health: HealthStatus;
    healthReason: string;
    lastSync: {
      id: number;
      syncType: 'scheduled' | 'manual' | 'single';
      startedAt: string;
      completedAt: string | null;
      status: 'running' | 'completed' | 'failed' | 'partial';
      totalEmployees: number;
      syncedCount: number;
      errorCount: number;
    } | null;
    employeeStatuses: {
      employeeId: number;
      employeeName: string;
      nmbrsEmployeeId: number;
      lastSyncedAt: string | null;
      syncStatus: 'active' | 'paused' | 'error';
    }[];
  };
}

export interface SyncLogEntry {
  id: number;
  syncType: 'scheduled' | 'manual' | 'single';
  startedAt: string;
  completedAt: string | null;
  status: 'running' | 'completed' | 'failed' | 'partial';
  totalEmployees: number;
  syncedCount: number;
  errorCount: number;
}

export interface SyncLogResponse {
  data: {
    logs: SyncLogEntry[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

export interface SyncLogDetailResponse {
  data: {
    id: number;
    syncType: 'scheduled' | 'manual' | 'single';
    triggeredBy: number | null;
    startedAt: string;
    completedAt: string | null;
    status: 'running' | 'completed' | 'failed' | 'partial';
    totalEmployees: number;
    syncedCount: number;
    errorCount: number;
    skippedCount: number;
    errors: {
      employeeId: number;
      error: string;
    }[];
  };
}

interface SyncTriggerResponse {
  data: {
    syncLogId: number;
    message: string;
    status: string;
  };
}

// ============================================
// Status & Log Hooks (Story 2.2)
// ============================================

/**
 * Hook to fetch the current sync status and health indicator.
 * Refreshes every 60 seconds.
 */
export function useSyncStatus() {
  return useQuery({
    queryKey: ['nmbrs', 'sync', 'status'],
    queryFn: () => apiRequest<SyncStatusResponse>('/nmbrs/sync/status'),
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

/**
 * Hook to fetch paginated sync history.
 */
export function useSyncLog(page: number = 1, limit: number = 20) {
  return useQuery({
    queryKey: ['nmbrs', 'sync', 'log', page, limit],
    queryFn: () =>
      apiRequest<SyncLogResponse>(`/nmbrs/sync/log?page=${page}&limit=${limit}`),
    staleTime: 60_000,
  });
}

/**
 * Hook to fetch a single sync log entry with error details.
 * Accepts optional query options (e.g. refetchInterval for polling).
 */
export function useSyncLogDetail(
  syncLogId: number,
  options?: { refetchInterval?: number | false }
) {
  return useQuery({
    queryKey: ['nmbrs', 'sync', 'log', syncLogId],
    queryFn: () =>
      apiRequest<SyncLogDetailResponse>(`/nmbrs/sync/log/${syncLogId}`),
    enabled: syncLogId > 0,
    refetchInterval: options?.refetchInterval,
  });
}

// ============================================
// Trigger Hooks (Story 2.4)
// ============================================

/**
 * Hook to trigger a full manual sync.
 * Invalidates sync status/log queries after mutation.
 */
export function useTriggerSync() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      apiRequest<SyncTriggerResponse>('/nmbrs/sync/trigger', {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nmbrs', 'sync'] });
    },
  });
}

/**
 * Hook to trigger a single employee sync.
 * Invalidates sync status/log queries after mutation.
 */
export function useTriggerEmployeeSync() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (employeeId: number) =>
      apiRequest<SyncTriggerResponse>(`/nmbrs/sync/trigger/${employeeId}`, {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nmbrs', 'sync'] });
      queryClient.invalidateQueries({ queryKey: ['nmbrs', 'overrides'] });
    },
  });
}
